import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Wifi, WifiOff, CheckCircle2, XCircle, Loader2, BarChart3, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { veritelService } from '@/lib/api';

const VeritelPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const res = await veritelService.getStats();
      setStats(res.data);
    } catch (e) {
      console.error('Error loading veritel stats:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleBulkVerify = async (checkWa) => {
    setVerifying(true);
    setLastResult(null);
    try {
      const res = await veritelService.verifyBulk(checkWa);
      setLastResult(res.data);
      toast.success(`Verificacion completada: ${res.data.verified} telefonos procesados`);
      loadStats();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error al verificar';
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const parts = d.split('T')[0].split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="veritel-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Phone className="w-5 h-5 text-teal-600" /> Verificacion de Telefonos
          </h3>
          <p className="text-sm text-slate-500">Valida los telefonos de tus clientes y detecta cuales tienen WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleBulkVerify(false)} disabled={verifying} variant="outline" data-testid="verify-phones-btn">
            {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
            Validar telefonos
          </Button>
          <Button onClick={() => handleBulkVerify(true)} disabled={verifying} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="verify-whatsapp-btn">
            {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
            Validar + WhatsApp
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total_with_phone || 0}</p>
            <p className="text-xs text-slate-500">Con telefono</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{stats.verified || 0}</p>
            <p className="text-xs text-slate-500">Verificados</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.unverified || 0}</p>
            <p className="text-xs text-slate-500">Sin verificar</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-xs text-slate-400 mb-1">Ultima verificacion</p>
            <p className="text-sm font-medium text-slate-700">{formatDate(stats.last_run)}</p>
          </Card>
        </div>
      )}

      {/* Type & WhatsApp Breakdown */}
      {stats && (stats.by_type && Object.keys(stats.by_type).length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {/* By Type */}
          <Card className="p-5">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-slate-500" /> Por tipo de linea
            </p>
            <div className="space-y-2">
              {Object.entries(stats.by_type).map(([tipo, count]) => {
                const colors = { CELULAR: 'bg-teal-100 text-teal-700', FIJO: 'bg-blue-100 text-blue-700', INVALIDO: 'bg-red-100 text-red-700' };
                return (
                  <div key={tipo} className="flex items-center justify-between">
                    <Badge className={`${colors[tipo] || 'bg-slate-100 text-slate-700'} font-medium`}>{tipo}</Badge>
                    <span className="text-sm font-semibold text-slate-900">{count}</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* WhatsApp */}
          <Card className="p-5">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Wifi className="w-4 h-4 text-green-600" /> WhatsApp
            </p>
            {Object.keys(stats.whatsapp || {}).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(stats.whatsapp).map(([status, count]) => {
                  const icons = { SI: <CheckCircle2 className="w-4 h-4 text-green-600" />, NO: <WifiOff className="w-4 h-4 text-red-500" />, ERROR: <AlertTriangle className="w-4 h-4 text-amber-500" /> };
                  const labels = { SI: 'Tiene WhatsApp', NO: 'No tiene', ERROR: 'Error', 'N/A': 'No verificado' };
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {icons[status] || <Phone className="w-4 h-4 text-slate-400" />}
                        <span className="text-sm text-slate-600">{labels[status] || status}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">Ejecuta "Validar + WhatsApp" para ver resultados</p>
            )}
          </Card>
        </div>
      )}

      {/* Last Run Result */}
      {lastResult && (
        <Card className="p-5 bg-teal-50 border-teal-200">
          <p className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" /> Resultado de la ultima verificacion
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-teal-600">Total procesados</p>
              <p className="font-bold text-teal-900">{lastResult.total}</p>
            </div>
            <div>
              <p className="text-teal-600">Actualizados</p>
              <p className="font-bold text-teal-900">{lastResult.verified}</p>
            </div>
            {lastResult.by_type && Object.entries(lastResult.by_type).map(([k, v]) => (
              <div key={k}>
                <p className="text-teal-600">{k}</p>
                <p className="font-bold text-teal-900">{v}</p>
              </div>
            ))}
            {lastResult.whatsapp && (
              <>
                <div>
                  <p className="text-green-600">Con WhatsApp</p>
                  <p className="font-bold text-green-800">{lastResult.whatsapp.SI || 0}</p>
                </div>
                <div>
                  <p className="text-red-600">Sin WhatsApp</p>
                  <p className="font-bold text-red-800">{lastResult.whatsapp.NO || 0}</p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!stats?.verified && !lastResult && (
        <Card className="p-12 text-center">
          <Phone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-2">No hay verificaciones aun</p>
          <p className="text-sm text-slate-400">Hace clic en "Validar + WhatsApp" para verificar todos los telefonos de tus clientes</p>
        </Card>
      )}
    </div>
  );
};

export default VeritelPanel;
